import { useState, useEffect, useMemo } from 'react';
import { useCurrentUserProfile } from '../hook/useCurrentUserProfile';
import logoIcon from '../assets/logo.png';

/**
 * Replace variables in template string with live values or fallback samples
 */
function replaceLiveVariables(text = '', values = {}) {
  if (!text) return '';
  let result = text;

  // Replace provided variable values
  Object.entries(values).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      const reg = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
      result = result.replace(reg, String(val));
    }
  });

  // Provide sensible realistic defaults for common tags if still unfilled
  result = result
    .replace(/{{\s*(?:customer_name|name)\s*}}/gi, values.name || 'John Doe')
    .replace(/{{\s*first_name\s*}}/gi, values.first_name || 'John')
    .replace(/{{\s*last_name\s*}}/gi, values.last_name || 'Doe')
    .replace(/{{\s*email\s*}}/gi, values.email || 'john.doe@example.com')
    .replace(/{{\s*phone\s*}}/gi, values.phone || '+91 98765 43210')
    .replace(/{{\s*company_name\s*}}/gi, values.company_name || 'Acme Fashion');

  return result;
}

export function EmailPhonePreview({
  template,
  subject: customSubject,
  body: customBody,
  variableValues = {},
  mediaFile = null,
  senderName: customSenderName,
  senderEmail: customSenderEmail,
  recipientEmail = '',
  time = '16:04',
  className = '',
}) {
  const {
    displayName,
    companyName: profileCompanyName,
    avatarUrl,
  } = useCurrentUserProfile();

  const [isStarred, setIsStarred] = useState(false);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreviewUrl(null);
      return undefined;
    }
    if (typeof mediaFile === 'string') {
      setMediaPreviewUrl(mediaFile);
      return undefined;
    }
    if (mediaFile instanceof Blob || mediaFile instanceof File) {
      const url = URL.createObjectURL(mediaFile);
      setMediaPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [mediaFile]);

  const companyName = useMemo(() => {
    return (
      customSenderName ||
      profileCompanyName ||
      localStorage.getItem('company_name') ||
      displayName ||
      'Demo Flexion Infotech'
    );
  }, [customSenderName, profileCompanyName, displayName]);

  const senderEmail = useMemo(() => {
    if (customSenderEmail) return customSenderEmail;
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u.email) return u.email;
      }
    } catch {
      // ignore
    }
    return 'demo@flexioninfotech.com';
  }, [customSenderEmail]);

  const displaySubject = useMemo(() => {
    const raw = customSubject || template?.subject || 'Email Subject';
    return replaceLiveVariables(raw, variableValues);
  }, [customSubject, template?.subject, variableValues]);

  const processedBody = useMemo(() => {
    const raw =
      customBody ||
      template?.body ||
      '<p>Hello,</p><p>This is a preview of your email message.</p>';
    let replaced = replaceLiveVariables(raw, variableValues);

    // If mediaFile uploaded, inject or replace image tags
    if (mediaPreviewUrl) {
      const imgTag = `<img src="${mediaPreviewUrl}" alt="Header" style="width: 100%; border-radius: 12px; max-height: 180px; object-fit: cover; margin-bottom: 12px;" />`;
      if (/{{header_image}}|{{image}}/i.test(replaced)) {
        replaced = replaced.replace(/{{header_image}}|{{image}}/gi, imgTag);
      }
    } else {
      replaced = replaced.replace(/{{header_image}}|{{image}}/gi, '');
    }

    return replaced;
  }, [customBody, template?.body, variableValues, mediaPreviewUrl]);

  const isHtml =
    typeof processedBody === 'string' &&
    processedBody.includes('<') &&
    processedBody.includes('>');

  return (
    <div
      className={`w-[290px] sm:w-[320px] xl:w-[340px] max-w-full h-[520px] sm:h-[640px] min-h-0 rounded-[34px] border-[8px] border-phone-bezel bg-white relative shadow-xl flex flex-col overflow-hidden select-none border-t-[10px] border-b-[10px] mx-auto ${className}`}
    >
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-4 bg-phone-bezel rounded-b-[13px] z-50 flex items-center justify-center pointer-events-none">
        <div className="w-6 h-0.5 bg-[#1a1a1a] rounded-full" />
      </div>

      {/* Mobile Screen Area */}
      <div className="w-full h-full min-h-0 flex flex-col overflow-hidden bg-white">
        {/* Status Bar */}
        <div className="h-5.5 w-full flex items-center justify-between px-3.5 text-[10px] font-bold text-[#111B21] z-40 bg-white select-none pt-0.5 shrink-0">
          <span>09:41</span>
          <div className="flex items-center gap-1">
            <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
              <path d="M2 22h20V2z" />
            </svg>
            <div className="w-3.5 h-2 border border-[#111B21]/60 rounded-sm flex items-center p-0.5 relative">
              <div className="h-full w-3/4 bg-[#111B21]/80 rounded-[1px]" />
            </div>
          </div>
        </div>

        {/* Gmail Top App Bar */}
        <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-white shrink-0 text-gray-700">
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
            aria-label="Back"
          >
            <svg
              className="w-5 h-5 text-gray-700"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div className="flex items-center gap-3 text-gray-600">
            {/* Archive */}
            <svg
              className="w-4 h-4 hover:text-gray-900 cursor-pointer"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-4a2 2 0 00-2 2v0a2 2 0 01-2 2h-4a2 2 0 01-2-2v0a2 2 0 00-2-2H4"
              />
            </svg>
            {/* Delete / Trash */}
            <svg
              className="w-4 h-4 hover:text-gray-900 cursor-pointer"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            {/* Mark unread */}
            <svg
              className="w-4 h-4 hover:text-gray-900 cursor-pointer"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            {/* More options */}
            <svg
              className="w-4 h-4 hover:text-gray-900 cursor-pointer"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </div>
        </div>

        {/* Email Subject Section */}
        <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2 shrink-0 bg-white">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            <h2 className="text-base sm:text-[17px] font-bold text-gray-900 leading-snug break-words">
              {displaySubject}
            </h2>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wider">
              Inbox
            </span>
          </div>
          {/* Star Icon */}
          <button
            type="button"
            onClick={() => setIsStarred((prev) => !prev)}
            className={`p-0.5 shrink-0 transition-colors focus:outline-none ${
              isStarred
                ? 'text-amber-400'
                : 'text-gray-400 hover:text-amber-400'
            }`}
            aria-label={isStarred ? 'Unstar' : 'Star'}
          >
            <svg
              className={`w-4 h-4 ${isStarred ? 'fill-amber-400 stroke-amber-400' : 'fill-none stroke-current'}`}
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        </div>

        {/* Sender Profile Row */}
        <div className="px-4 py-2 flex items-center justify-between gap-2 border-b border-gray-100 bg-white shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-primary/10 flex items-center justify-center border border-primary/20">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={logoIcon}
                  alt="Logo"
                  className="w-5 h-5 object-contain"
                />
              )}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-bold text-gray-900 truncate leading-tight">
                {companyName}
              </span>
              <span className="text-[11px] text-gray-500 truncate leading-tight">
                {senderEmail}
                {recipientEmail ? ` • to ${recipientEmail}` : ''}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 text-gray-500">
            <span className="text-[11px] font-medium text-gray-400">
              {time}
            </span>
            {/* Quick Reply Arrow */}
            <svg
              className="w-3.5 h-3.5 hover:text-gray-900 cursor-pointer"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
              />
            </svg>
            {/* More */}
            <svg
              className="w-3.5 h-3.5 hover:text-gray-900 cursor-pointer"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </div>
        </div>

        {/* Email Body Scroll Area */}
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 bg-white text-gray-800 text-xs leading-relaxed">
          {/* Header Image Upload Preview */}
          {mediaPreviewUrl && (
            <div className="mb-3 rounded-xl overflow-hidden border border-gray-100 shadow-sm max-h-48 w-full">
              <img
                src={mediaPreviewUrl}
                alt="Campaign Media"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Rendered Email Content */}
          {isHtml ? (
            <div
              className="email-rendered-preview break-words font-sans space-y-2 [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_p]:mb-2 [&_img]:max-w-full [&_img]:rounded-lg [&_a]:text-primary [&_button]:cursor-default"
              dangerouslySetInnerHTML={{ __html: processedBody }}
            />
          ) : (
            <div className="whitespace-pre-wrap break-words font-sans space-y-2 text-gray-800">
              {processedBody}
            </div>
          )}
        </div>

        {/* Bottom Action Bar: Reply & Forward */}
        <div className="p-3 bg-white border-t border-gray-100 flex items-center justify-center gap-2 shrink-0">
          <button
            type="button"
            className="flex-1 py-1.5 px-3 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-700 transition-colors focus:outline-none"
          >
            <svg
              className="w-3.5 h-3.5 text-gray-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
              />
            </svg>
            <span>Reply</span>
          </button>
          <button
            type="button"
            className="flex-1 py-1.5 px-3 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-700 transition-colors focus:outline-none"
          >
            <svg
              className="w-3.5 h-3.5 text-gray-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 10H11a8 8 0 00-8 8v2M21 10l-6-6m6 6l-6 6"
              />
            </svg>
            <span>Forward</span>
          </button>
        </div>

        {/* Bottom Indicator Bar */}
        <div className="pb-1.5 bg-white shrink-0 flex justify-center">
          <div className="w-28 h-1 bg-black/40 rounded-full" />
        </div>
      </div>
    </div>
  );
}
